import { z } from 'zod';
import { cuid, dinheiro, isoDate, paginacao, valorFinito } from './primitives.js';
import { modalidadeFrete, type ModalidadeFrete } from './plano.js';
import { opcoesSchema } from './precificacao.js';
import type { OrigemLucro } from './tabelas-preco.js';
import type { ComparativoCustoResponse } from './custo-atualizado.js';
import type { OrcamentoEventoResponse, OrigemOrcamentoDto, StatusVersaoDto, VersaoResumoResponse } from './orcamento-versoes.js';

export const statusOrcamento = z.enum(['RASCUNHO', 'ENVIADO', 'EM_NEGOCIACAO', 'APROVADO', 'PERDIDO']);
export type StatusOrcamentoDto = z.infer<typeof statusOrcamento>;

export const categoriaMotivoPerdido = z.enum(['PRECO', 'PRAZO', 'CONCORRENTE', 'DESISTIU', 'OUTRO']);
export type CategoriaMotivoPerdido = z.infer<typeof categoriaMotivoPerdido>;

/**
 * Transicoes validas (fonte unica; a API e o banco as impoem, o front so as le para oferecer opcoes).
 * APROVADO e PERDIDO sao finais.
 */
export const TRANSICOES_ORCAMENTO: Record<StatusOrcamentoDto, readonly StatusOrcamentoDto[]> = {
  RASCUNHO: ['ENVIADO', 'PERDIDO'],
  ENVIADO: ['EM_NEGOCIACAO', 'APROVADO', 'PERDIDO'],
  EM_NEGOCIACAO: ['APROVADO', 'PERDIDO'],
  APROVADO: [],
  PERDIDO: [],
};

/**
 * POST /orcamentos/:id/situacao. `motivo` e obrigatorio para PERDIDO (a API responde 422 MOTIVO_OBRIGATORIO); nas demais
 * situacoes e opcional (fica no historico) e `categoria` so vale para PERDIDO (422 CATEGORIA_INVALIDA).
 */
export const mudarSituacaoSchema = z.object({
  para: statusOrcamento,
  motivo: z.string().trim().min(1).max(1000).optional(),
  categoria: categoriaMotivoPerdido.optional(),
});
export type MudarSituacaoDto = z.infer<typeof mudarSituacaoSchema>;

export interface OrcamentoSituacaoResponse {
  id: string;
  de: StatusOrcamentoDto | null;
  para: StatusOrcamentoDto;
  motivo: string | null;
  categoria: CategoriaMotivoPerdido | null;
  usuarioId: string | null;
  /** Nome/e-mail de quem mudou, quando o usuario existe. */
  usuarioNome: string | null;
  createdAt: string;
}

/**
 * Frete do orcamento (Sessao 19). Forma antiga: `receita`/`custo` em R$ do orcamento inteiro (valem como antes). Forma nova, com `modalidade`:
 * CIF = o vendedor paga o frete (custo = frete NET; `total` em R$ ou `porT` em R$ por unidade de custo, t); FOB = o cliente paga (custo 0).
 * Frete net pela regra do plano da empresa (LEGAL com o ICMS da tabela, ou FATOR FIXO). `destacado`: o frete e cobrado em linha propria
 * (receita = `receita` informada ou o frete total; paga tributo); sem ele (padrao) o frete vai embutido no preco, sem linha na proposta.
 * `total`, `porT`, `transportadora` e `transportadoraCredita` sao custo/operacao: exigem custo.ver para informar e para ver.
 */
export const freteOrcamentoSchema = z
  .object({
    receita: valorFinito.optional(),
    custo: valorFinito.optional(),
    modalidade: modalidadeFrete.optional(),
    transportadora: z.string().trim().min(1).max(120).optional(),
    total: dinheiro.optional(),
    porT: dinheiro.optional(),
    transportadoraCredita: z.boolean().optional(),
    destacado: z.boolean().optional(),
  })
  .refine((v) => !(v.total !== undefined && v.porT !== undefined), { message: 'Informe o frete total OU o frete por t, nao os dois', path: ['porT'] })
  .refine((v) => v.modalidade !== undefined || (v.total === undefined && v.porT === undefined && v.transportadora === undefined && v.transportadoraCredita === undefined && v.destacado === undefined), {
    message: 'Informe a modalidade do frete (CIF ou FOB)',
    path: ['modalidade'],
  })
  .refine((v) => v.modalidade === undefined || v.custo === undefined, { message: 'Com modalidade o custo do frete e calculado (frete net): nao informe `custo`', path: ['custo'] });
export type FreteOrcamentoDto = z.infer<typeof freteOrcamentoSchema>;

/** Detalhe do frete gravado no snapshot e exibido: modalidade/transportadora/destacado sempre; valores de custo so com custo.ver. */
export interface FreteDetalheResponse {
  modalidade: ModalidadeFrete;
  transportadora: string | null;
  destacado: boolean;
  /** Custo (so com custo.ver): frete total, R$/t informado, frete net e a regra usada. */
  total?: string;
  porT?: string | null;
  freteNet?: string;
  regra?: 'LEGAL' | 'FATOR_FIXO';
  transportadoraCredita?: boolean;
}

/** Corpo de criacao e de edicao (PUT regrava o orcamento inteiro, snapshot e itens juntos). */
export const orcamentoSchema = z.object({
  data: isoDate,
  /** Sessao 15 (6b, P5): data prevista de faturamento (fato gerador): decide o regime da reforma. Ausente = a data do orcamento. */
  dataFaturamento: isoDate.optional(),
  tabelaId: cuid,
  condicaoId: cuid,
  clienteId: cuid.optional(),
  /** Sessao 30: UF de destino da operacao (ICMS e IBS por UF). Ausente = a UF do endereco principal do cliente; informada, vale a do orcamento e vai no snapshot. */
  ufDestino: z.string().regex(/^[A-Z]{2}$/, 'UF com 2 letras maiusculas').optional(),
  descricao: z.string().trim().min(1).max(300).optional(),
  frete: freteOrcamentoSchema.optional(),
  consideraFreteLucro: z.boolean().default(false),
  itens: z
    .array(
      z.object({
        itemId: cuid,
        quantidade: dinheiro,
        /** Preco negociado COM IPI, a prazo (9.5.5). */
        precoUnitario: dinheiro,
      }),
    )
    .min(1)
    .max(500),
  options: opcoesSchema.optional(),
});
export type OrcamentoDto = z.infer<typeof orcamentoSchema>;

/** Filtros comuns da listagem e do resumo (so o que restringe o conjunto de orcamentos). */
const filtrosOrcamentos = {
  clienteId: cuid.optional(),
  /** Numero exato (so digitos) ou trecho do nome do cliente / descricao. */
  q: z.string().trim().min(1).max(100).optional(),
  /** Periodo pela data do orcamento, inclusivo nas duas pontas. */
  dataDe: isoDate.optional(),
  dataAte: isoDate.optional(),
  /** `true`: so orcamentos com lucro abaixo da meta (faixa laranja ou vermelha do motor). */
  abaixoDaMeta: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  /** `true`: so propostas vencidas (ENVIADO/EM_NEGOCIACAO com a validade da versao de referencia vencida; derivado, nao e situacao). */
  vencido: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  /** Propostas (ENVIADO/EM_NEGOCIACAO) que vencem dentro de N dias a partir de hoje (inclusive hoje); ainda nao vencidas. */
  vencendo: z.coerce.number().int().min(1).max(60).optional(),
  /** Origem do orcamento; sem o filtro, todas (o painel de margens, ao contrario, usa so APP por padrao). */
  origem: z.enum(['APP', 'IMPORTADO_XARP']).optional(),
};

export const ordenarOrcamentos = z.enum(['numero', 'data', 'cliente', 'total', 'lucro', 'status']);

export const listarOrcamentosSchema = paginacao.extend({
  ...filtrosOrcamentos,
  status: statusOrcamento.optional(),
  /** Faixa de numeros (inclusiva): com `limit=1` o `total` da lista dá a posicao ("n de total") e o vizinho. */
  numeroDe: z.coerce.number().int().min(1).optional(),
  numeroAte: z.coerce.number().int().min(1).optional(),
  ordenar: ordenarOrcamentos.default('numero'),
  direcao: z.enum(['asc', 'desc']).default('desc'),
});
export type ListarOrcamentosQuery = z.infer<typeof listarOrcamentosSchema>;

/** GET /orcamentos.xlsx: os mesmos filtros e a mesma ordem da lista, sem paginacao (o servidor exporta todas as linhas, ate o teto). */
export const exportarOrcamentosSchema = listarOrcamentosSchema.omit({ limit: true, offset: true });
export type ExportarOrcamentosQuery = z.infer<typeof exportarOrcamentosSchema>;

/** GET /orcamentos/resumo: os mesmos filtros da lista, menos status (o resumo e por status) e paginacao. */
export const resumoOrcamentosSchema = z.object(filtrosOrcamentos);
export type ResumoOrcamentosQuery = z.infer<typeof resumoOrcamentosSchema>;

/** GET /orcamentos: pagina + total real do filtro (`total` independe de limit/offset). */
export interface ListaOrcamentosResponse {
  itens: OrcamentoResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface ResumoStatusOrcamentos {
  quantidade: number;
  /** Soma de `total` (dinheiro, 2 casas) dos orcamentos do status. */
  total: string;
}

/**
 * Agregados no servidor (SQL), sobre as saidas ja gravadas do motor; nada e recalculado.
 * Respeita q/cliente/periodo/abaixoDaMeta, nunca o status.
 */
export interface ResumoOrcamentosResponse {
  porStatus: Record<z.infer<typeof statusOrcamento>, ResumoStatusOrcamentos>;
  /** Todos os status somados. */
  geral: ResumoStatusOrcamentos;
  /** Em aberto = RASCUNHO + ENVIADO + EM_NEGOCIACAO (soma feita na API). */
  emAberto: ResumoStatusOrcamentos;
  /** Vencidos (derivado da validade da versao de referencia): subconjunto de ENVIADO + EM_NEGOCIACAO. A API sempre o envia; opcional no tipo so para nao quebrar consumidores anteriores ao E11. */
  vencidos?: ResumoStatusOrcamentos;
  /** Lucro medio dos APROVADOS, ponderado pela receita bruta (fracao, 4 casas); null sem aprovados com lucro. */
  lucroMedioAprovado: string | null;
  /** Dos orcamentos com faixa de lucro: quantos estao abaixo da meta (laranja/vermelho) e a base. */
  abaixoDaMeta: { quantidade: number; base: number };
}

/** Campos "so com custo.ver" sao OMITIDOS (nao vem null) para quem nao tem a permissao (VENDEDOR, LEITURA). */
/** Margem de contribuicao do item no orcamento (Sessao 15). `valor` (R$ da linha) so com custo.ver. */
export interface OrcamentoItemMc {
  /** MC% = MC / preco sem IPI (fracao); null se o preco e zero. */
  pct: string | null;
  faixa: 'GREEN' | 'ORANGE' | 'RED';
  /** MC <= 0: o item vende no prejuizo marginal, mesmo com o lucro do pedido no verde. */
  prejuizoMarginal: boolean;
  valor?: string;
}

/** MC do orcamento: ponderada pela receita; com frete detalhado (Sessao 19) ja desconta o frete net (e soma a receita de frete liquida de tributo). */
export interface OrcamentoMc {
  pct: string | null;
  faixa: 'GREEN' | 'ORANGE' | 'RED';
  /** Quantos itens estao com prejuizo marginal. */
  itensPrejuizoMarginal: number;
  valor?: string;
}

export interface OrcamentoItemResponse {
  /** Sessao 15: margem de contribuicao do item. */
  mc?: OrcamentoItemMc;
  seq: number;
  itemId: string;
  /** null para MERCADORIA (custo de aquisicao). So com custo.ver. */
  fichaId?: string | null;
  /** Origem do custo (snapshot): 'ficha' | 'aquisicao' | 'composicao'. So com custo.ver. */
  costSource?: string | null;
  descricao: string;
  unidade: string;
  quantity: string;
  /** So com custo.ver. */
  unitCost?: string;
  unitPrice: string;
  ipiRate: string;
  ipiValor: string;
  line: string;
  /** So com custo.ver. */
  lineExact?: string;
  /** So com custo.ver. */
  netUnitPrice?: string;
  /** Lucro do item em precisao cheia (so com custo.ver; os demais usam `exibicao.itens[].lucro`). */
  profit?: string;
  /** So com custo.ver. */
  profitValue?: string;
  faixaLucro: string;
}

/** Item do orcamento ja arredondado para exibir (dinheiro 2 casas, fracoes 4). Derivado do resultado do motor. */
export interface OrcamentoItemExibicao {
  seq: number;
  itemId: string;
  codigo: string;
  descricao: string;
  unidade: string;
  /** So com custo.ver. */
  costSource?: string | null;
  quantidade: string;
  /** Negociado (com IPI, a prazo). */
  precoUnitario: string;
  /** So com custo.ver. */
  custoUnitario?: string;
  /** Total da linha (round2 do motor). */
  total: string;
  ipiRate: string;
  ipiValor: string;
  /** Lucro do item (fracao, 4 casas) e faixa do motor. */
  lucro: string;
  /** Lucro em R$ do item. So com custo.ver. */
  lucroValor?: string;
  faixa: 'green' | 'orange' | 'red';
  /** Sessao 15 (6b): "+ CBS/IBS" da linha (round2, nao informativos) e o total da NF da linha. */
  cbsIbsValor?: string;
  nfLinha?: string;
}

/** Reforma tributaria no orcamento (Sessao 15, 6b): regime da data de faturamento, CBS/IBS por fora e total da NF. */
export interface OrcamentoReformaExibicao {
  /** Data que decidiu o regime (faturamento previsto, ou a do orcamento). */
  dataOperacao: string;
  regime: 'LEGACY' | 'DUAL_2026' | 'TRANSICAO' | 'NOVO_2033';
  etapa: string;
  /** 2026: CBS/IBS destacados, nao somam ao total (NT 2025.002). */
  informativo: boolean;
  /** Aliquota de referencia estimada (hipotetica) em uso. */
  aliquotaEstimada: boolean;
  /** O praticado inclui CBS/IBS (flag da tabela): o total da NF e o proprio total. */
  precoIncluiCbsIbs: boolean;
  /** "+ CBS/IBS" (Σ round2 por item, nao informativos). */
  cbsIbsTotal: string;
  /** Imposto Seletivo por fora (item marcado), quando houver. */
  isTotal: string;
  /** Total da NF: total + CBS/IBS + IS (flag desligada); = total (flag ligada). */
  nfTotal: string;
}

/** Totais e lucro prontos para exibir (fracoes com 4 casas = percentual com 2). Mesma forma na simulacao e no gravado. */
export interface OrcamentoExibicao {
  /** Meta e minimo da tabela (fracao). */
  metaLucro: string;
  minimoLucro: string | null;
  /** De onde vieram meta e minimo (DP6); ausente em orcamento gravado antes da hierarquia (= da tabela). */
  metaLucroOrigem?: OrigemLucro;
  minimoLucroOrigem?: OrigemLucro;
  receitaTotal: string;
  receitaItens: string;
  quantidadeTotal: string;
  freteReceita: string;
  /** So com custo.ver. */
  freteCusto?: string;
  /** Modalidade/transportadora/destacado (e, com custo.ver, total, R$/t e frete net). Ausente = frete na forma antiga (so receita/custo). */
  freteDetalhe?: FreteDetalheResponse;
  /** Lucro sem frete; null sem receita. So com custo.ver (com `lucroComFrete` entregaria o custo do frete). */
  lucro?: string | null;
  /** So com custo.ver. */
  lucroComFrete?: string;
  /** O que o orcamento exibe: com frete se `consideraFreteLucro`, senao sem. */
  lucroExibido: string | null;
  faixa: 'green' | 'orange' | 'red' | null;
  itens: OrcamentoItemExibicao[];
  /** Sessao 15 (6b): reforma tributaria (ausente em orcamento gravado antes dela). */
  reforma?: OrcamentoReformaExibicao;
}

/** POST /orcamentos/simular: o que o motor devolve para o payload, sem gravar nada. */
export interface OrcamentoSimuladoResponse {
  motorVersao: string;
  data: string;
  cliente: { id: string; nome: string; documento: string | null } | null;
  consideraFreteLucro: boolean;
  warnings: unknown[];
  /** Sessao 15 (6b, P5): a data prevista de faturamento cruza uma mudanca de regime. */
  avisosReforma?: Array<{ code: string; message: string; fronteira: string; etapa: string; paraDualTransicao?: boolean }>;
  exibicao: OrcamentoExibicao;
  /** Entrada e saida cruas do motor: so com custo.ver. */
  snapshot?: unknown;
  resultado?: unknown;
}

/** Um parametro que o orcamento congelou (Sessao 28): o rotulo, o valor JA formatado pela API e de onde ele veio. `sensivel` = custo, despesa ou lucro (some para quem nao ve custo). */
export interface ParametroCongelado {
  grupo: string;
  rotulo: string;
  valor: string;
  origem: string;
  sensivel: boolean;
}

/** POST /orcamentos/:id/versoes/:n/atualizar-parametros: `confirmar:false` so mostra antes x depois; `true` regrava o rascunho com os parametros vigentes. */
export const atualizarParametrosSchema = z.object({ confirmar: z.boolean().default(false) });
export type AtualizarParametrosDto = z.infer<typeof atualizarParametrosSchema>;
export interface AtualizarParametrosResponse {
  aplicado: boolean;
  /** So o que mudou. */
  mudancas: Array<{ grupo: string; rotulo: string; antes: string; depois: string; origem: string }>;
  total: { antes: string; depois: string };
  /** Lucro exibido (%), ja formatado; null = sem lucro calculavel. */
  lucro: { antes: string | null; depois: string | null };
}

export interface OrcamentoResponse {
  /** Parametros congelados na versao, em lista legivel (so no detalhe). */
  parametros?: ParametroCongelado[];
  /** Sessao 15 (6b, P5): data prevista de faturamento gravada na versao; null = a data do orcamento. */
  dataFaturamento?: string | null;
  /** Sessao 30: UF de destino usada no calculo (ICMS e IBS por UF) e de onde veio; null = sem UF (cliente sem endereco e nenhuma informada). Orcamentos antigos: ausente. */
  ufDestino?: string | null;
  ufDestinoOrigem?: 'ENDERECO_PRINCIPAL' | 'INFORMADA' | null;
  /** Avisos de regime (ORCAMENTO_CRUZA_REGIME): a validade ou o faturamento cruzam uma mudanca de regime da reforma. */
  avisosReforma?: Array<{ code: string; message: string; fronteira: string; etapa: string; paraDualTransicao?: boolean }>;
  /** Sessao 15: margem de contribuicao do orcamento (so no detalhe). */
  mc?: OrcamentoMc;
  id: string;
  /** Numero do orcamento (sequencial por empresa). */
  numero: number;
  /** `33392-v2`: numero + versao exibida. */
  numeroExibido: string;
  status: z.infer<typeof statusOrcamento>;
  origem: OrigemOrcamentoDto;
  /** Versao que esta resposta descreve (no detalhe: o rascunho aberto, senao a de referencia; na lista: a de referencia). */
  versaoId: string;
  versaoNumero: number;
  versaoStatus: StatusVersaoDto;
  /** true = nao editavel (versao diferente de RASCUNHO). */
  somenteLeitura: boolean;
  /** true = esta e a versao que vale para lista, kanban, margens e exportacoes. */
  versaoReferencia: boolean;
  /** Quantidade de versoes (sem as rascunhos descartados; a v1 sempre conta). */
  versoesTotal: number;
  /** Validade DA VERSAO (gravada no envio = data do envio + validade padrao). Nula em rascunho. */
  validaAte: string | null;
  validadeDias: number;
  /** Derivado da validade (ENVIADO/EM_NEGOCIACAO com `validaAte` passada); nunca e uma situacao. */
  vencido: boolean;
  enviadaEm: string | null;
  /** sha256 do PDF congelado da versao (nulo em rascunho, importada e migrada). */
  pdfSha256: string | null;
  /** Aba Versoes (so no detalhe; vazio na listagem). */
  versoes: VersaoResumoResponse[];
  /** Linha do tempo completa: situacoes do pipeline + ciclo das versoes (so no detalhe). `situacoes` traz so as do pipeline. */
  eventos: OrcamentoEventoResponse[];
  data: string;
  descricao: string | null;
  clienteId: string | null;
  /** Nome/documento da epoca (snapshot), nao do cadastro vivo. */
  cliente: { id: string; nome: string; documento: string | null } | null;
  vigenciaId?: string;
  /** Tabela de preco do orcamento (pela vigencia usada). */
  tabelaId: string;
  condicaoId: string;
  freteReceita: string | null;
  /** So com custo.ver. */
  freteCusto?: string | null;
  consideraFreteLucro: boolean;
  grossRevenue: string;
  /** So com custo.ver. */
  grossRevenueExact?: string;
  total: string;
  quantity: string;
  /** Lucro sem frete e com frete: so com custo.ver (os dois juntos entregam o custo do frete). */
  grossProfit?: string | null;
  grossProfitFrete?: string;
  displayedProfitRate: string | null;
  faixaLucro: string | null;
  warnings: unknown[];
  itens: OrcamentoItemResponse[];
  motorVersao: string;
  aprovadoEm: string | null;
  aprovadoPorId: string | null;
  motivoPerdido: string | null;
  motivoPerdidoCategoria: CategoriaMotivoPerdido | null;
  statusAlteradoEm: string;
  /** Historico de SITUACOES do pipeline (so no detalhe: criar, editar, buscar e mudar situacao; vazio na listagem). */
  situacoes: OrcamentoSituacaoResponse[];
  createdAt: string;
  updatedAt: string;
  snapshot?: unknown;
  resultado?: unknown;
  /** Totais/lucro/itens prontos para exibir (todas as respostas: criar, editar, buscar, status e listagem). */
  exibicao: OrcamentoExibicao;
  /**
   * Custo na epoca x hoje (E12). SO existe na versao de referencia ENVIADA de proposta ENVIADO/EM_NEGOCIACAO dentro da
   * validade; nas demais situacoes (rascunho, vencido, aprovado, perdido, importado) a chave nao existe. Sem `custo.ver`
   * vem so `acimaDoLimiar`, as faixas e `mudouDeCor` (nunca X%/Y%).
   */
  comparativoCusto?: ComparativoCustoResponse;
  /** Atalho para o icone do cartao/linha: `comparativoCusto.acimaDoLimiar`. Mesma regra de existencia. */
  alertaCusto?: boolean;
  /**
   * So em versao RASCUNHO do detalhe: `true` = a leitura recalculou a versao com os custos de hoje (sem gravar); `false` = nao deu
   * (cadastro incompleto) e os valores sao os ultimos gravados. Lista e kanban mostram sempre o ultimo valor gravado do rascunho.
   */
  aoVivo?: boolean;
}

export interface RecalculoOrcamentoResponse {
  id: string;
  motorVersao: string;
  reproduz: boolean;
  resultado: unknown;
}
